import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Card, CardBody, CircularProgress, Tab, Tabs } from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { BsDatabaseFill } from "react-icons/bs";
import { Tooltip } from "react-tooltip";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";

// Rows come from the API Monday first (Python's date.weekday()).
const WEEKDAYS = [
  { short: "Mon", full: "Monday", color: "#5B93FF" },
  { short: "Tue", full: "Tuesday", color: "#05CD99" },
  { short: "Wed", full: "Wednesday", color: "#FFD66B" },
  { short: "Thu", full: "Thursday", color: "#FF8F6B" },
  { short: "Fri", full: "Friday", color: "#AE8FF7" },
  { short: "Sat", full: "Saturday", color: "#4FD1C5" },
  { short: "Sun", full: "Sunday", color: "#F76FB2" },
];
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const HOUR_LABEL_EVERY = 3;
const TOOLTIP_ID = "calls-heatmap-tooltip";

// The bar chart groups by time slot, one bar per weekday: 24 hours x 7 days
// would be 168 bars. Slots are [fromHour, toHour).
const TIME_SLOTS = [
  { label: "Before 9am", from: 0, to: 9 },
  { label: "Morning (9am-12pm)", from: 9, to: 12 },
  { label: "Lunch (12pm-2pm)", from: 12, to: 14 },
  { label: "Afternoon (2pm-6pm)", from: 14, to: 18 },
  { label: "After 6pm", from: 18, to: 24 },
];

// Primary (#4318FF) with an opacity floor, so a quiet-but-non-empty slot stays
// distinguishable from a slot without any call.
const cellColor = (value, max) => {
  if (!value || !max) return "#F5F5F5";
  return `rgba(67, 24, 255, ${0.12 + 0.88 * (value / max)})`;
};

const CallsHeatmapSection = ({ title = "Calls by day and hour" }) => {
  const { t, i18n } = useTranslation();
  const { calls_heatmap, loading_calls } = useSelector((state) => state.analytics.analytics);
  const [view, setView] = useState("heatmap");

  const averages = calls_heatmap?.averages || [];
  const weekdayAverages = calls_heatmap?.weekday_averages || [];
  const max = Math.max(0, ...averages.flat());
  const hasData = !loading_calls && max > 0;
  const formatAverage = (value) =>
    value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });

  const renderHeatmap = () => (
    <div className="w-full overflow-x-auto">
      <div className="flex items-center justify-end gap-[8px] text-xs text-gray-400 mb-[12px]">
        <span>0</span>
        <div
          className="w-[120px] h-[8px] rounded-full"
          style={{
            background: `linear-gradient(to right, ${cellColor(Number.EPSILON, 1)}, ${cellColor(
              1,
              1
            )})`,
          }}
        />
        <span>{formatAverage(max)}</span>
      </div>
      <div
        className="grid gap-[3px] min-w-[640px]"
        style={{ gridTemplateColumns: "40px repeat(24, minmax(0, 1fr)) 72px" }}
      >
        <div />
        {HOURS.map((hour) => (
          <div
            key={`h-${hour}`}
            className="text-[11px] text-gray-400 leading-none pb-[4px]"
          >
            {hour % HOUR_LABEL_EVERY === 0 ? `${hour}h` : ""}
          </div>
        ))}
        <div className="text-[11px] text-gray-400 leading-none pb-[4px] text-right">
          {t("Avg / day")}
        </div>
        {WEEKDAYS.map((weekday, weekdayIndex) => (
          <React.Fragment key={weekday.short}>
            <div className="text-xs text-gray-500 flex items-center">{t(weekday.short)}</div>
            {HOURS.map((hour) => {
              const value = averages[weekdayIndex]?.[hour] || 0;
              return (
                <div
                  key={`${weekday.short}-${hour}`}
                  className="h-[28px] rounded-[3px]"
                  style={{ backgroundColor: cellColor(value, max) }}
                  data-tooltip-id={TOOLTIP_ID}
                  data-tooltip-content={`${t(weekday.full)} ${hour}h - ${
                    hour + 1
                  }h : ${formatAverage(value)} ${t("calls on average")}`}
                />
              );
            })}
            <div className="text-sm font-medium text-[#404040] flex items-center justify-end">
              {formatAverage(weekdayAverages[weekdayIndex] || 0)}
            </div>
          </React.Fragment>
        ))}
      </div>
      <Tooltip id={TOOLTIP_ID} />
    </div>
  );

  const renderBarChart = () => {
    const data = {
      labels: TIME_SLOTS.map((slot) => t(slot.label)),
      datasets: WEEKDAYS.map((weekday, weekdayIndex) => ({
        label: t(weekday.full),
        data: TIME_SLOTS.map((slot) =>
          (averages[weekdayIndex] || [])
            .slice(slot.from, slot.to)
            .reduce((accumulator, value) => accumulator + value, 0)
        ),
        backgroundColor: weekday.color,
        borderWidth: 0,
        borderRadius: 3,
        maxBarThickness: 24,
      })),
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      // Hovering a slot lists every weekday at once, which is the comparison.
      interaction: { mode: "index", intersect: false },
      plugins: {
        datalabels: { display: false },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            padding: 12,
            font: { family: "DM Sans", size: 11 },
            color: "#737373",
          },
        },
        tooltip: {
          callbacks: {
            label: (context) =>
              ` ${context.dataset.label} : ${formatAverage(context.parsed.y)} ${t(
                "calls on average"
              )}`,
          },
        },
      },
      scales: {
        x: {
          ticks: { color: "#404040", font: { family: "DM Sans", size: 12 } },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { color: "#737373" },
          grid: { color: "#F5F5F5" },
        },
      },
    };

    return (
      <div className="w-full h-[320px] relative">
        <Bar
          data={data}
          options={options}
        />
      </div>
    );
  };

  return (
    <Card
      className="w-full h-full rounded-md shadow-custom-light px-[32px] py-[20px]"
      shadow="none"
    >
      <CardBody className="w-full p-0 box-border">
        <div className="w-full flex items-start justify-between gap-[16px] flex-wrap mb-[20px]">
          <div className="flex flex-col gap-[6px]">
            <h6 className="text-[14px] font-medium text-gray-400 leading-none">{t(title)}</h6>
            <span className="text-xs text-gray-400">
              {t("Average number of calls per day over the period")}
            </span>
          </div>
          <Tabs
            size="sm"
            aria-label={t(title)}
            selectedKey={view}
            onSelectionChange={setView}
          >
            <Tab
              key="heatmap"
              title={t("Heatmap")}
            />
            <Tab
              key="bars"
              title={t("Bars")}
            />
          </Tabs>
        </div>
        {loading_calls && (
          <div className="flex flex-col justify-center items-center w-full h-[260px] m-auto text-sm">
            <CircularProgress
              aria-label={t("Loading...")}
              color="primary"
              label={t("Loading...")}
            />
          </div>
        )}
        {hasData && view === "heatmap" && renderHeatmap()}
        {hasData && view === "bars" && renderBarChart()}
        {!loading_calls && max === 0 && (
          <div className="w-full h-[260px] flex flex-col items-center justify-center">
            <BsDatabaseFill className="text-gray-300 w-12 h-12" />
            <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default CallsHeatmapSection;
